import { Card, Empty, Flex, Tag, Typography } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  ThunderboltOutlined,
  FireOutlined,
} from "@ant-design/icons";
import { useFeatureFlag } from "eligibility/hooks/useFeatureFlag";
import { useEligibilityChecks } from "eligibility/queries";
import { EligibilityVerdict, IEligibilityCheck } from "eligibility/models";
import variables from "../../_variables.scss";

interface Props {
  organizationId?: string;
  projectId: string;
}

const VERDICT: Record<
  EligibilityVerdict,
  { color: string; label: string; Icon: typeof CheckCircleFilled }
> = {
  pass: { color: "success", label: "Qualifies", Icon: CheckCircleFilled },
  fail: { color: "error", label: "Doesn't qualify", Icon: CloseCircleFilled },
  nodata: { color: "warning", label: "Needs data", Icon: ExclamationCircleFilled },
};

const fmt = (v: number | null) => (v == null ? "—" : v.toLocaleString());

const CheckItem = ({ check, first }: { check: IEligibilityCheck; first: boolean }) => {
  const v = VERDICT[check.verdict] ?? VERDICT.nodata;
  const { Icon } = v;
  return (
    <div
      style={{
        paddingTop: first ? 0 : 10,
        marginTop: first ? 0 : 10,
        borderTop: first ? undefined : "1px solid #f0f0f0",
      }}
    >
      <Flex justify="space-between" align="center" gap={8}>
        <Tag color={v.color} icon={<Icon />} style={{ marginInlineEnd: 0 }}>
          {v.label}
        </Tag>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {check.createdAt
            ? new Date(check.createdAt).toLocaleString(undefined, {
                dateStyle: "short",
                timeStyle: "short",
              })
            : ""}
        </Typography.Text>
      </Flex>
      <Flex gap={16} wrap style={{ marginTop: 6 }}>
        <Typography.Text style={{ fontSize: 12 }}>
          <ThunderboltOutlined /> {fmt(check.avgPeakDemandKw)} kW
        </Typography.Text>
        <Typography.Text style={{ fontSize: 12 }}>
          <FireOutlined /> {fmt(check.annualUsageTherms)} therms
        </Typography.Text>
      </Flex>
      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
        {check.billFormat}
      </Typography.Text>
    </div>
  );
};

// Compact, graphic widget for the project detail sidebar (under Notes). Rendered
// only when the tenant has the eligibility_checker flag on.
export const EligibilityChecksPanel = ({ organizationId, projectId }: Props) => {
  const enabled = useFeatureFlag("eligibility_checker");
  const { data: checks, isLoading } = useEligibilityChecks(organizationId, projectId);

  if (!enabled) return null;

  return (
    <Card bordered={false} loading={isLoading} data-testid="eligibility-checks-panel">
      <Flex vertical gap={variables.gap}>
        <h1 style={{ marginBottom: 0 }}>Eligibility Checks</h1>
        {checks && checks.length > 0 ? (
          <div>
            {checks.map((c, i) => (
              <CheckItem key={c.id} check={c} first={i === 0} />
            ))}
          </div>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No eligibility checks saved"
          />
        )}
      </Flex>
    </Card>
  );
};
